<template>
  <a>
    <font-awesome-icon :disable="!this.fetchKey"
                       @click="open"
                       icon="file-alt"
                       v-show="!isLoading" />

    <font-awesome-icon icon="spinner" spin v-show="isLoading"  />

    <pdf-viewer :show.sync="showPdfViewer" :file-path="dataUri" :file-name="documentDetails.documentName" />
    <image-viewer :show.sync="showImageViewer"  :file-path="dataUri" :file-name="documentDetails.documentName" />
  </a>

</template>

<script>
import { last, isEmpty } from 'lodash-es'

import PdfViewer from '@/components/ModalDisplayPdf'
import ImageViewer from '@/components/ModalDisplayImages'
import DocumentApi from '@/api-interface/document'
import fileDownload from '@/helpers/downloadBlob'

export default {
  name: 'ViewDocumentIcon',

  props: {
    document: {
      type: Object
    },
    s3Key: {
      type: String
    }
  },

  data() {
    return {
      isLoading: false,
      fullDetails: null,
      showPdfViewer: false,
      showImageViewer: false,
      dataUri: ''
    }
  },

  computed: {
    /**
     * Convenience flag to check if we have the document detials or not
     * @returns {boolean}
     */
    hasDocumentDetails() {
      return !isEmpty(this.documentDetails)
    },

    /**
     * Normalized document details, so that other methods don't have to worry about if the document was fetched
     * or passed in by the parent.
     *
     * @returns {object}
     */
    documentDetails() {
      return this.document || this.fullDetails || {}
    },

    /**
     * The unique key to use when fetching full document details.
     * Make the use of the documentKey vs s3Key transparent.
     * @returns {string}
     */
    fetchKey() {
      if (this.hasDocumentDetails) {
        return this.documentDetails.documentKey
      } else {
        return this.s3Key
      }
    },

    /**
     * Flag to indicate if the document is of type PDF
     *
     * @returns {boolean}
     */
    isPdf() {
      if (this.hasDocumentDetails) {
        const extension = last(this.documentDetails.documentName.split('.')).toLowerCase()
        return extension === 'pdf'
      }
    },

    /**
     * Flag to indicate if the document is an image type
     *
     * @returns {boolean}
     */
    isImage() {
      if (this.hasDocumentDetails) {
        const imageExtensions = ['jpg', 'jpeg', 'gif', 'png', 'bmp']
        const extension = last(this.documentDetails.documentName.split('.')).toLowerCase()
        return imageExtensions.includes(extension)
      }
    }
  },

  components: {
    PdfViewer,
    ImageViewer
  },

  methods: {
    /**
     * Fetch full document details (included the binary strip)
     * @returns {Promise<void>}
     */
    async fetchDetails() {
      this.isLoading = true
      try {
        this.fullDetails = await DocumentApi.getS3ByKey(this.fetchKey)
      } catch (error) {
        console.error(`Error downloading document ${this.fetchKey} : `, error)
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Attempt to open the document
     * @returns {Promise<void>}
     */
    async open() {
      if (isEmpty(this.fullDetails)) {
        await this.fetchDetails()
      }

      // PDF and image files get displayed in-line, all other file types are downloaded
      if (this.isPdf) {
        this.showPdfViewer = true
        this.dataUri = `data:application/pdf;base64,${this.fullDetails.documentData}`
      } else if (this.isImage) {
        this.showImageViewer = true
        this.dataUri = `data:application/octet-stream;base64,${this.fullDetails.documentData}`
      } else {
        this.download()
      }
    },

    /**
     * Down the document
     * @returns {Promise<void>}
     */
    async download() {
      return fileDownload({
        fileString: this.fullDetails.documentData,
        userFriendlyName: this.fullDetails.documentName
      })
    }
  }
}
</script>

<style scoped>
a {
  cursor: pointer !important;
}
</style>
